/**
 * Model Detail Page
 * Shows reusable model info + IMEI instances
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createDevice, fetchModel, fetchModelInstances } from './api';
import { deviceDetailPath } from './deviceLookupHonesty';
import type { Device } from './types';

export default function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [attachImei, setAttachImei] = useState('');
  const [attachGatewayId, setAttachGatewayId] = useState('');
  const modelQuery = useQuery({
    queryKey: ['iot', 'model', id],
    queryFn: () => fetchModel(id!),
    enabled: !!id,
  });
  const instancesQuery = useQuery({
    queryKey: ['iot', 'model', id, 'instances'],
    queryFn: () => fetchModelInstances(id!),
    enabled: !!id,
    refetchInterval: 20000,
  });

  const model = modelQuery.data;
  const instances = instancesQuery.data?.items || [];

  const attachMutation = useMutation({
    mutationFn: async () => {
      if (!attachImei.trim()) throw new Error('IMEI is required');
      const payload = {
        deviceId: attachImei.trim(),
        type: model?.type || 'telemetry_sensor',
        templateId: model?.deviceTemplateId || null,
        metadata: {
          imei: attachImei.trim(),
          manufacturer: model?.manufacturer || null,
          model: model?.model || null,
          model_product_id: model?.id || null,
          model_id: model?.id || null,
        },
        gatewayId: attachGatewayId.trim() || null,
      };
      return createDevice(payload);
    },
    onSuccess: () => {
      setAttachImei('');
      setAttachGatewayId('');
      instancesQuery.refetch();
    },
  });

  if (modelQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (modelQuery.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load model: {String(modelQuery.error)}</p>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">Model not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/iotshack/models" className="text-gray-400 hover:text-gray-600">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{model.name}</h1>
          <p className="text-gray-600 mt-1">
            {(model.manufacturer || 'Unknown')} • {(model.model || 'Unknown')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Model Details</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Type</dt>
                <dd className="text-gray-900">{model.type}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Source</dt>
                <dd className="text-gray-900">{model.source || 'pim'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Template</dt>
                <dd className="text-gray-900">{model.templateId || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Telemetry Template</dt>
                <dd className="text-gray-900">{model.telemetryTemplateId || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Device Template</dt>
                <dd className="text-gray-900">{model.deviceTemplateId || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Decoder</dt>
                <dd className="text-gray-900">{model.decoderId || '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Device Instances</h2>
              <span className="text-xs text-gray-500">
                {instances.length} IMEIs
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              An IMEI on this model is not a device page until a device is registered for this app.
            </p>
            {instancesQuery.isLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {instances.length === 0 ? (
                  <div className="text-sm text-gray-500">No devices registered for this model.</div>
                ) : (
                  instances.map((device) => <InstanceRow key={device._id} device={device} />)
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 space-y-2">
                <div className="text-xs font-semibold text-gray-700">Attach IMEI to this model</div>
                <input
                  value={attachImei}
                  onChange={(e) => setAttachImei(e.target.value)}
                  placeholder="IMEI (device identifier)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <input
                  value={attachGatewayId}
                  onChange={(e) => setAttachGatewayId(e.target.value)}
                  placeholder="Gateway ID (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={() => attachMutation.mutate()}
                  disabled={!attachImei.trim() || attachMutation.isPending}
                  className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300"
                >
                  {attachMutation.isPending ? 'Attaching…' : 'Attach IMEI'}
                </button>
                {attachMutation.isError && (
                  <div className="text-xs text-red-600">{String(attachMutation.error)}</div>
                )}
              </div>
              <Link
                to="/iotshack/agent"
                className="block w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
              >
                Onboard another device
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InstanceRow({ device }: { device: Device }) {
  const statusColors = {
    online: 'bg-emerald-100 text-emerald-800',
    offline: 'bg-rose-100 text-rose-800',
    unknown: 'bg-gray-100 text-gray-800',
  };
  const href = deviceDetailPath(device);
  const imei = device.metadata?.imei ? String(device.metadata.imei) : '';
  const label = device.deviceId || imei || '—';
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm">
      <div>
        {href ? (
          <Link to={href} className="text-blue-700 hover:text-blue-800">
            {label}
          </Link>
        ) : (
          <span className="font-mono text-gray-900">{label}</span>
        )}
        {imei && device.deviceId && imei !== device.deviceId && (
          <div className="text-xs text-gray-500">IMEI {imei}</div>
        )}
        <div className="text-xs text-gray-500">{device.gatewayId || 'No gateway'}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[device.status]}`}>
          {device.status}
        </span>
        <span className="text-xs text-gray-500">
          {device.lastSeen ? new Date(device.lastSeen).toLocaleString() : 'No telemetry'}
        </span>
      </div>
    </div>
  );
}
